import { List, Map } from 'immutable';
import type { RecordOf } from 'immutable';
import { storage } from '@/lib/storage';
import type { CompanyPermissions, RoleModificators, UserProfileResponse } from '@/domain/env/schemas';
import type {
  AppShape,
  EnvShape,
  PayModuleCompanyPermissionShape,
  PermissionsShape,
  UserFeaturesShape,
} from '@/domain/env/types';
import {
  appFactory,
  CompanyPermissionsFactory,
  EnvFactory,
  OrgBotFactory,
  PayModuleCompanyPermissionFactory,
  PermissionsFactory,
  PermissionsNotesFactory,
  roleModificatorsFactory,
  UserFeaturesFactory,
} from '@/domain/env/factories';

type ApProfile = UserProfileResponse['AP']['profile'];

type PermissionsInput = Partial<Omit<PermissionsShape, 'notes'>> & {
  notes?: Partial<ApProfile['permissions']['notes']>;
};

export function permissionsAdapter({ notes, ...rest }: PermissionsInput) {
  const notesRecord = PermissionsNotesFactory({
    isCreate: notes?.create,
    isUpdate: notes?.update,
    isDelete: notes?.delete,
  });
  return PermissionsFactory({
    ...rest,
    notes: notesRecord,
  });
}

export function featuresAdapter(features: Partial<UserFeaturesShape>) {
  return UserFeaturesFactory(features);
}

export function orgBotAdapter({ userID, displayName, picture }: Partial<ApProfile['organizationBot']>) {
  const pictureObj = picture ? { picture } : {};
  return OrgBotFactory({
    userId: userID,
    username: displayName,
    ...pictureObj,
  });
}

export function roleModificatorsAdapter(data: Partial<RoleModificators>) {
  return roleModificatorsFactory(data);
}

export const companyPermissionsAdapter = (raw: { [companyId: string]: CompanyPermissions }) =>
  Map(raw).map((entry) => CompanyPermissionsFactory(entry));

export function appsAdapter(data: UserProfileResponse) {
  return Object.keys(data).reduce(
    (acc, key) => acc.set(key, appFactory(data[key as keyof UserProfileResponse])),
    Map<string, RecordOf<AppShape>>(),
  );
}

export const payModuleCompanyPermissionsAdapter = (permissions: Array<Partial<PayModuleCompanyPermissionShape>>) =>
  List(permissions.map((permission) => PayModuleCompanyPermissionFactory(permission)));

type EnvInput = Partial<Omit<EnvShape, 'permissions' | 'roleModificators'>> &
  Partial<RoleModificators> & { permissions?: PermissionsInput };

export function envAdapter({ permissions, restricted, limited, readonly, ...rest }: EnvInput) {
  const permissionsRecord = permissionsAdapter(permissions || {});
  const roleModificatorsRecord = roleModificatorsAdapter({ restricted, limited, readonly });
  const workSpaceStr = storage().workSpaceType().get();
  const workSpace = workSpaceStr ? JSON.parse(workSpaceStr) : {};
  const persistedBackUrl = storage().backUrl().get();

  return EnvFactory({
    ...rest,
    persistedBackUrl,
    permissions: permissionsRecord,
    roleModificators: roleModificatorsRecord,
    workSpace: Map(workSpace),
  });
}
